import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FILTER_HASH = 'filtro';
const STORAGE_KEY = 'jserranoCategoryFiltersScroll';
const SCROLL_OFFSET = 80;

let categoryStackTriggers = [];
let categoryStackResizeBound = false;
let categoryStackResizeTimeout = null;

const clearCategoryProductsStack = () => {
  categoryStackTriggers.forEach((trigger) => trigger.kill());
  categoryStackTriggers = [];

  document.querySelectorAll('.category-products__card').forEach((card) => {
    card.style.zIndex = '';
  });
};

const setupCategoryProductsStack = () => {
  const productsSection = document.querySelector('.category-products');
  if (!productsSection) {
    clearCategoryProductsStack();
    return;
  }

  const visibleCards = Array.from(
    productsSection.querySelectorAll('.category-products__card:not(.is-hidden)')
  );

  clearCategoryProductsStack();

  if (!visibleCards.length) {
    ScrollTrigger.refresh();
    return;
  }

  visibleCards.forEach((card, index) => {
    const isLastCard = index === visibleCards.length - 1;

    gsap.set(card, { zIndex: index + 1 });

    const trigger = ScrollTrigger.create({
      trigger: card,
      start: 'top top',
      end: 'bottom top',
      pin: true,
      pinSpacing: isLastCard,
      invalidateOnRefresh: true,
    });

    categoryStackTriggers.push(trigger);
  });

  ScrollTrigger.refresh();
};

const scheduleCategoryProductsStackSetup = () => {
  window.requestAnimationFrame(() => {
    setupCategoryProductsStack();
  });
};

const bindCategoryProductsStackResize = () => {
  if (categoryStackResizeBound) {
    return;
  }

  categoryStackResizeBound = true;

  window.addEventListener(
    'resize',
    () => {
      if (categoryStackResizeTimeout) {
        window.clearTimeout(categoryStackResizeTimeout);
      }

      categoryStackResizeTimeout = window.setTimeout(() => {
        scheduleCategoryProductsStackSetup();
      }, 120);
    },
    { passive: true }
  );
};

const safeStorageGet = () => {
  try {
    return sessionStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

const safeStorageSet = () => {
  try {
    sessionStorage?.setItem(STORAGE_KEY, '1');
  } catch {
    // ignore
  }
};

const safeStorageClear = () => {
  try {
    sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const scrollToFilters = () => {
  const section = document.getElementById(FILTER_HASH);
  if (!section) {
    return;
  }

  const top = section.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({
    top: Math.max(0, top - SCROLL_OFFSET),
    behavior: 'smooth',
  });
};

const shouldScroll = () => {
  const hash = window.location.hash?.toLowerCase() ?? '';
  return hash.includes(`#${FILTER_HASH}`) || safeStorageGet() === '1';
};

const notifyNavTabs = () => {
  const tabs = document.querySelectorAll('[data-category-nav-scroll]');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      safeStorageSet();
    });
  });
};

const buildFilterMap = (form) => {
  const map = {};
  form.querySelectorAll('input[type="checkbox"][data-filter-group]').forEach((checkbox) => {
    if (!checkbox.checked) {
      return;
    }

    const group = checkbox.dataset.filterGroup;
    const value = checkbox.dataset.filterValue;
    if (!group || !value) {
      return;
    }

    if (!map[group]) {
      map[group] = [];
    }

    map[group].push(value);
  });

  return map;
};

const matchesFilters = (filters, cardFilters) => {
  if (Object.keys(filters).length === 0) {
    return true;
  }

  return Object.entries(filters).every(([, values]) =>
    values.some((value) => cardFilters.includes(value))
  );
};

const parseCardFilters = (attribute) =>
  (attribute ?? '')
    .split(/\s+/)
    .map((item) => item.trim())
    .filter(Boolean);

const syncSelectAll = (form, selectAll) => {
  if (!selectAll) {
    return;
  }

  const inputs = Array.from(form.querySelectorAll('input[type="checkbox"][data-filter-group]'));
  const checked = inputs.filter((input) => input.checked).length;
  selectAll.checked = inputs.length > 0 && checked === inputs.length;
  selectAll.indeterminate = checked > 0 && checked < inputs.length;
};

export function initCategoryFilters() {
  const form = document.querySelector('.category-filters__form');
  const selectAll = form?.querySelector('[data-category-filters-select-all]');
  const cards = Array.from(document.querySelectorAll('[data-category-product-filters]'));
  const emptyState = document.querySelector('.category-products__empty');
  const countElement = document.querySelector('.category-products__count');
  const countTemplate =
    countElement?.dataset.categoryProductsCountTemplate ?? 'Encontramos (%s) coleções para você';
  const numberFormatter = new Intl.NumberFormat('pt-BR');

  notifyNavTabs();
  bindCategoryProductsStackResize();

  if (!form) {
    scheduleCategoryProductsStackSetup();

    if (shouldScroll()) {
      scrollToFilters();
      safeStorageClear();
    }
    return;
  }

  const apply = () => {
    const filters = buildFilterMap(form);
    let visibleCount = 0;

    clearCategoryProductsStack();

    cards.forEach((card) => {
      const cardFilters = parseCardFilters(card.dataset.categoryProductFilters);
      const shouldShow = matchesFilters(filters, cardFilters);
      card.classList.toggle('is-hidden', !shouldShow);
      if (shouldShow) {
        visibleCount += 1;
      }
    });

    if (emptyState) {
      const showEmpty = Object.keys(filters).length > 0 && visibleCount === 0;
      emptyState.classList.toggle('is-visible', showEmpty);
      emptyState.hidden = !showEmpty;
    }

    if (countElement) {
      countElement.textContent = countTemplate.replace('%s', numberFormatter.format(visibleCount));
    }

    scheduleCategoryProductsStackSetup();
  };

  const handleChange = () => {
    syncSelectAll(form, selectAll);
    apply();
  };

  form.addEventListener('submit', (event) => event.preventDefault());

  form.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    if (target.matches('[data-category-filters-select-all]')) {
      const { checked } = target;
      form.querySelectorAll('input[type="checkbox"][data-filter-group]').forEach((checkbox) => {
        checkbox.checked = checked;
      });
    }

    handleChange();
  });

  handleChange();

  if (shouldScroll()) {
    scrollToFilters();
    safeStorageClear();
  } else {
    safeStorageClear();
  }
}
